import React, { LegacyRef, useContext } from 'react';
import t from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';

// redux
import { wrapThunkAction } from 'redux/utils';
import { addTableRow } from 'redux/actions/quizzes';
import { getFlatCourseAliases } from 'redux/selectors/course';

import NvTooltip from 'shared/components/nv-tooltip';
import QuizContext, { SavingIndicator } from 'quizzes/components/context';
import NvIcon from 'shared/components/nv-icon';
import { css } from '@emotion/core';
import { quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { gray4, gray6, highTide, primary, white } from 'styles/global_defaults/colors';
import { config } from '../../../../../config/config.json';

type AddRowButtonProps = {
  addRowRef: LegacyRef<HTMLButtonElement>
  isAddRowDisabled: boolean;
  setIsAddRowDisabled: Function;
};
const AddRowButton = (props: AddRowButtonProps) => {
  const { addRowRef, isAddRowDisabled, setIsAddRowDisabled } = props;
  const {
    currentQuestion,
    savingIndicatorTimeoutRef,
    setSavingStatus,
    canMakeStructuralChanges,
  } = useContext(QuizContext);
  const { numRows } = currentQuestion;
  const isMaxRowLimitReached = numRows === 20;
  const dispatch = useDispatch();
  const courseAliases = useSelector(getFlatCourseAliases);

  const isButtonDisabled = isAddRowDisabled
    || isMaxRowLimitReached
    || !canMakeStructuralChanges;

  const buttonStyles = css`
    &.add-row-button {
      position: absolute;
      left: 0%;
      top: 100%;
      padding-top: ${quarterSpacing}px;
      transform: translateY(5px);
      background-color: ${isButtonDisabled ? gray6 : primary};
      color: ${isButtonDisabled ? gray4 : white};
      border: none;
      border-radius: ${quarterSpacing}px;
      height: ${standardSpacing}px;
    }
    &.add-row-button:hover {
      background-color: ${isButtonDisabled ? gray6 : highTide};
    }
  `;

  const handleAddRow = () => {
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    setIsAddRowDisabled(true);
    wrapThunkAction(dispatch(addTableRow(
      {
        questionId: currentQuestion.id,
        rowIndex: numRows - 1,
      },
    ))).then((res) => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      setIsAddRowDisabled(false);
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const getTooltipText = () => {
    if (!canMakeStructuralChanges) {
      return t.QUIZZES.TABLE_QUESTION.DISABLED_TOOLTIP(courseAliases);
    }
    if (isMaxRowLimitReached) {
      return t.QUIZZES.TABLE_QUESTION.MAX_ROW_REACHED();
    }

    return t.QUIZZES.TABLE_QUESTION.ADD_ROW();
  };

  return (
    <NvTooltip
      text={getTooltipText()}
    >
      <button
        css={buttonStyles}
        type='button'
        onClick={handleAddRow}
        ref={addRowRef}
        disabled={isButtonDisabled}
        className='add-row-button d-flex align-items-center justify-content-center'
        data-qa={config.pendo.activities.quiz.questionModal.tableQuestion.addRow}
      >
        <NvIcon
          size='xs-smallest'
          icon='create-new-post'
        />
      </button>
    </NvTooltip>
  );
};

export default AddRowButton;
